import React, { useMemo } from 'react';
import {
  BadgeCheck,
  Binary,
  CalendarClock,
  CircleDot,
  Hash,
  Link2,
  MapPin,
  ToggleLeft,
  Type,
  UserRound,
} from 'lucide-react';
import { ResultColumn } from '../../Types/DataServiceType';
import { CopyButton } from './DataResultGrid';

interface Props {
  columns: ResultColumn[];
  row: unknown[];
  tableLabel: string;
}

type GroupKey = 'identity' | 'location' | 'dates' | 'links' | 'identifiers' | 'other';

interface Field {
  label: string;
  value: unknown;
  column: ResultColumn;
  icon: typeof Type;
}

const GROUP_META: Record<GroupKey, { title: string; accent: string }> = {
  identity: { title: 'Identity', accent: 'text-cyan-400' },
  location: { title: 'Location', accent: 'text-emerald-400' },
  dates: { title: 'Dates', accent: 'text-amber-400' },
  links: { title: 'Links', accent: 'text-violet-400' },
  identifiers: { title: 'Identifiers', accent: 'text-sky-400' },
  other: { title: 'Other fields', accent: 'text-slate-400' },
};

const GROUP_ORDER: GroupKey[] = ['identity', 'location', 'dates', 'links', 'identifiers', 'other'];

const KEYWORDS: Record<Exclude<GroupKey, 'other'>, RegExp> = {
  identity:
    /^(first|middle|last|full|user)?_?(name|email|phone|mobile|contact|title|headline|role|position|designation|username|login)$/i,
  location: /(city|state|country|region|address|zip|postal|pincode|location|country_code)/i,
  dates: /(date|time|_at$|_on$|created|updated|deleted|expires|start|end)/i,
  links: /(url|uri|link|website|portfolio|linkedin|github|resume|cv)/i,
  identifiers: /^(id|uuid|slug|code|key|token|hash|reference|ref)$/i,
};

const matches = (column: ResultColumn, pattern: RegExp): boolean =>
  pattern.test(column.label) ||
  (column.typeName ? pattern.test(column.typeName) : false);

const groupFor = (column: ResultColumn): GroupKey => {
  for (const key of GROUP_ORDER) {
    if (key === 'other') continue;
    if (matches(column, KEYWORDS[key])) return key;
  }
  return 'other';
};

const iconFor = (column: ResultColumn, group: GroupKey): typeof Type => {
  const type = (column.typeName ?? '').toLowerCase();
  if (group === 'identity') return UserRound;
  if (group === 'location') return MapPin;
  if (group === 'dates') return CalendarClock;
  if (group === 'links') return Link2;
  if (group === 'identifiers') return Hash;
  if (type.includes('bool')) return ToggleLeft;
  if (type.includes('json') || column.label.toLowerCase().includes('json')) return Binary;
  if (type.includes('uuid') || /[0-9a-f]{8}-[0-9a-f]{4}/i.test(String(column.label))) return CircleDot;
  return Type;
};

const humanize = (label: string): string =>
  label
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

const isEmpty = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

const renderValue = (value: unknown, column: ResultColumn) => {
  if (isEmpty(value)) {
    return <span className="text-slate-600">Not set</span>;
  }

  if (typeof value === 'boolean') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold ${
          value
            ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
            : 'border-slate-700 bg-slate-800/60 text-slate-400'
        }`}
      >
        <BadgeCheck size={12} />
        {value ? 'Yes' : 'No'}
      </span>
    );
  }

  if (Array.isArray(value)) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {value.map((item, index) => (
          <span
            key={index}
            className="rounded-lg border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-[11px] text-slate-200"
          >
            {String(item)}
          </span>
        ))}
      </div>
    );
  }

  if (typeof value === 'object') {
    return (
      <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-words rounded-xl border border-slate-800 bg-slate-950 p-3 text-[11px] leading-relaxed text-slate-300">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  const text = String(value);
  const isUrl = /^https?:\/\//i.test(text);
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text);
  const isProse = /json|description|summary|body|content|notes?|message/i.test(column.label);

  if (isUrl) {
    return (
      <a
        href={text}
        target="_blank"
        rel="noreferrer noopener"
        className="break-all text-xs text-cyan-400 underline decoration-cyan-500/30 underline-offset-4 hover:text-cyan-300"
      >
        {text}
      </a>
    );
  }

  if (isProse) {
    return (
      <p className="max-h-64 overflow-y-auto whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-200">
        {text}
      </p>
    );
  }

  return (
    <p
      className={`break-words text-sm ${isUuid ? 'font-mono text-xs text-sky-300' : 'text-slate-200'}`}
    >
      {text}
    </p>
  );
};

/**
 * Detail renderer for tables that do not have a purpose-built screen.
 *
 * <p>The result grid is a debugging surface: raw column labels, values clipped
 * to one line, no grouping. That is right for scanning many rows and wrong for
 * understanding one, so this reshapes a single row into labelled fields grouped
 * by what the column means.</p>
 */
const DynamicRecordView: React.FC<Props> = ({ columns, row, tableLabel }) => {
  const groups = useMemo(() => {
    const buckets = new Map<GroupKey, Field[]>();

    columns.forEach((column, index) => {
      const group = groupFor(column);
      const bucket = buckets.get(group) ?? [];
      bucket.push({ label: humanize(column.label), value: row[index], column, icon: iconFor(column, group) });
      buckets.set(group, bucket);
    });

    return GROUP_ORDER.map((key) => ({ key, fields: buckets.get(key) ?? [] })).filter(
      (entry) => entry.fields.length > 0
    );
  }, [columns, row]);

  return (
    <div className="space-y-8">
      {groups.map(({ key, fields }) => (
        <section key={key}>
          <div className="mb-3 flex items-center gap-3">
            <h3 className={`text-[11px] font-black uppercase tracking-[0.2em] ${GROUP_META[key].accent}`}>
              {GROUP_META[key].title}
            </h3>
            <span className="h-px flex-1 bg-slate-800" />
            <span className="text-[11px] font-bold text-slate-600">
              {fields.length} field{fields.length === 1 ? '' : 's'}
            </span>
          </div>

          <dl className="grid gap-3 sm:grid-cols-2">
            {fields.map((field) => (
              <div
                key={field.label}
                className="group rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-3 transition hover:border-slate-700"
              >
                <dt className="mb-1.5 flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <field.icon size={12} className="shrink-0" />
                    <span className="truncate">{field.label}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    {field.column.primaryKey && (
                      <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest text-amber-400">
                        Key
                      </span>
                    )}
                    {!isEmpty(field.value) && !Array.isArray(field.value) && typeof field.value !== 'object' && (
                      <CopyButton text={String(field.value)} label="Copy" />
                    )}
                  </span>
                </dt>
                <dd>{renderValue(field.value, field.column)}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}

      <p className="border-t border-slate-800 pt-4 text-[11px] font-bold text-slate-600">
        {tableLabel} · {columns.length} column{columns.length === 1 ? '' : 's'} · every value comes
        straight from the row the access allowlist already permitted.
      </p>
    </div>
  );
};

export default DynamicRecordView;
